import React, { useState, useEffect } from 'react';
import { useShippingLabels } from '../../hooks/useShippingLabels';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { ShippingLabel } from '../../types/shipping';
import {
  X,
  Printer,
  Download,
  ExternalLink,
  Package,
  MapPin,
  Copy,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';

interface LabelReprintModalProps {
  labelId: string;
  onClose: () => void;
  onPrinted?: () => void;
}

export const LabelReprintModal: React.FC<LabelReprintModalProps> = ({
  labelId,
  onClose,
  onPrinted,
}) => {
  const { getLabelById, updatePrintCount } = useShippingLabels();
  const [label, setLabel] = useState<ShippingLabel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchLabel = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedLabel = await getLabelById(labelId);
        if (!fetchedLabel) {
          setError('Label not found. It may have been deleted or you don\'t have access to it.');
        }
        setLabel(fetchedLabel);
      } catch (err) {
        console.error('Error fetching label:', err);
        setError('Failed to load label. Please try again.');
        setLabel(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLabel();
  }, [labelId]);

  const handlePrint = async () => {
    if (!label?.label_pdf_base64) {
      setError('PDF data is not available for this label. Cannot print.');
      return;
    }

    setPrinting(true);
    setError(null);

    try {
      const byteCharacters = atob(label.label_pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        setError('Print window was blocked. Please allow popups for this site and try again.');
        URL.revokeObjectURL(url);
        setPrinting(false);
        return;
      }

      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(url);
        setPrinting(false);
      };

      // Fallback: if onload doesn't fire, try printing after a short delay
      setTimeout(() => {
        if (printWindow && !printWindow.closed) {
          try {
            printWindow.print();
          } catch (e) {
            console.error('Error printing:', e);
            setError('Failed to open print dialog. Please try downloading the PDF instead.');
          }
          URL.revokeObjectURL(url);
          setPrinting(false);
        }
      }, 1000);

      // Update print count (don't wait for it to complete)
      updatePrintCount(labelId).catch(err => {
        console.error('Error updating print count:', err);
        // Don't show error to user for this - it's not critical
      });

      onPrinted?.();
    } catch (err) {
      console.error('Error printing label:', err);
      setError('Failed to print label. The PDF data may be corrupted. Please try downloading instead.');
      setPrinting(false);
    }
  };

  const handleDownload = () => {
    if (!label?.label_pdf_base64) return;

    const byteCharacters = atob(label.label_pdf_base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `shipping-label-${label.barcode}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyBarcode = () => {
    if (!label) return;
    navigator.clipboard.writeText(label.barcode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'generated':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'printed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const trackingUrl = label
    ? `https://postnl.nl/tracktrace/?B=${label.tracking_code}&P=${label.recipient_postal_code}&D=${label.recipient_country_code}&T=C`
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Reprint Label
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error && !label ? (
            <div className="text-center py-12">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Error Loading Label
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </p>
              <Button onClick={onClose} variant="secondary">
                Close
              </Button>
            </div>
          ) : !label ? (
            <div className="text-center py-12">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Label not found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                This label may have been deleted or you don't have access to it.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {getStatusIcon(label.status)}
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {label.status}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Created {format(new Date(label.created_at), 'MMM d, yyyy HH:mm')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Recipient
                    </p>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">
                      {label.recipient_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Delivery Address
                    </p>
                    <p className="text-gray-900 dark:text-gray-100">
                      {label.recipient_street} {label.recipient_house_number}
                      {label.recipient_house_number_ext && ` ${label.recipient_house_number_ext}`}
                    </p>
                    <p className="text-gray-900 dark:text-gray-100">
                      {label.recipient_postal_code} {label.recipient_city}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {label.recipient_country_code}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Barcode
                      </p>
                      <p className="font-mono text-lg text-gray-900 dark:text-gray-100">
                        {label.barcode}
                      </p>
                    </div>
                    <button
                      onClick={handleCopyBarcode}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Copy barcode"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                  {label.reference && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      Reference: {label.reference}
                    </p>
                  )}
                </div>

                {label.print_count > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This label has been printed {label.print_count} time{label.print_count > 1 ? 's' : ''}
                    {label.last_printed_at && (
                      <>, last on {format(new Date(label.last_printed_at), 'MMM d, yyyy HH:mm')}</>
                    )}
                  </p>
                )}
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300 flex-1">
                      {error}
                    </p>
                    <button
                      onClick={() => setError(null)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {!label.label_pdf_base64 && label.status !== 'cancelled' && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        PDF Data Not Available
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        The PDF data for this label is not available. You can still track the shipment using the tracking code.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {label.status !== 'cancelled' && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button 
                    onClick={handlePrint} 
                    variant="primary" 
                    disabled={!label.label_pdf_base64 || printing}
                    loading={printing}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {printing ? 'Printing...' : 'Print Label'}
                  </Button>
                  <Button onClick={handleDownload} variant="secondary" disabled={!label.label_pdf_base64}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary inline-flex items-center"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Track
                  </a>
                </div>
              )}

              {label.status === 'cancelled' && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    This label has been cancelled and cannot be reprinted.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
